import { soundService } from '../services/soundService';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { User, AuthContextType } from '../types';
import { cacheManager } from '../services/cacheManager';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const saved = localStorage.getItem('libritui_user');
        return saved ? JSON.parse(saved) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const supabaseUser = session.user;
                const newUser: User = {
                    id: supabaseUser.id,
                    email: supabaseUser.email || '',
                    displayName: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'Usuário',
                    photoURL: supabaseUser.user_metadata?.avatar_url
                };
                setUser(newUser);
                localStorage.setItem('libritui_user', JSON.stringify(newUser));
            }
            setLoading(false);
        };

        checkSession();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (session?.user) {
                const supabaseUser = session.user;
                const newUser: User = {
                    id: supabaseUser.id,
                    email: supabaseUser.email || '',
                    displayName: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'Usuário',
                    photoURL: supabaseUser.user_metadata?.avatar_url
                };
                setUser(newUser);
                localStorage.setItem('libritui_user', JSON.stringify(newUser));
            }

            if (event === 'SIGNED_OUT') {
                setUser(null);
                localStorage.removeItem('libritui_user');
                cacheManager.clearAll();
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Porto Seguro 5.2: Sync email only once per mount/login on user change
    const lastSyncIdRef = React.useRef<string | null>(null);
    useEffect(() => {
        if (!user || lastSyncIdRef.current === user.id) return;
        // Convidado de desenvolvimento (só localhost): não sincroniza perfil nem pede notificação
        if (import.meta.env.DEV && user.id === 'dev-guest') return;
        
        lastSyncIdRef.current = user.id;
        
        const syncEmailAndToken = async () => {
            const SYNC_KEY = `libritui_last_synced_email_${user.id}`;
            const TOKEN_SYNC_KEY = `libritui_last_fcm_${user.id}`;
            
            try {
                const { profileService } = await import('../services/profileService');
                const updates: any = {};
                
                if (localStorage.getItem(SYNC_KEY) !== user.email) {
                    updates.email = user.email;
                }
                
                if (soundService.getPrefs().push) try {
                    const { requestFirebaseNotificationPermission } = await import('../services/firebase');
                    const token = await requestFirebaseNotificationPermission();
                    if (token && localStorage.getItem(TOKEN_SYNC_KEY) !== token) {
                        updates.fcm_token = token;
                    }
                } catch (e) { console.warn('Firebase request failed', e); }

                if (Object.keys(updates).length > 0) {
                    await profileService.updateProfile(user.id, updates);
                    if (updates.email) localStorage.setItem(SYNC_KEY, updates.email);
                    if (updates.fcm_token) localStorage.setItem(TOKEN_SYNC_KEY, updates.fcm_token);
                }
                

            } catch (e) {
                console.warn("AuthContext: Isolated sync failed:", e);
            }
        };

        // Delay it slightly to let components settle
        const timer = setTimeout(syncEmailAndToken, 2000);
        return () => clearTimeout(timer);
    }, [user?.id]);

    const login = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                },
                redirectTo: window.location.origin
            }
        });
        if (error) throw error;
    };

    const logout = async () => {
        await supabase.auth.signOut();
        setUser(null);
        localStorage.removeItem('libritui_user');
        cacheManager.clearAll();
    };

    const updateUser = (updates: Partial<User>) => {
        if (user) {
            const newUser = { ...user, ...updates };
            setUser(newUser);
            localStorage.setItem('libritui_user', JSON.stringify(newUser));
        }
    };

    const value: AuthContextType = React.useMemo(() => ({
        user,
        loading,
        login,
        logout,
        updateUser
    }), [user, loading]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
